import React, {FC} from 'react';
import root from 'window-or-global';
import {Theme} from '@material-ui/core/styles';
import {VisualizationsTheme} from '@epic-visualizations/theme';
import {compactDateFormatter, ValueFormatter} from '@epic-visualizations/charts';

import {TickProps} from './types';

const AVERAGE_TICK_WIDTH = 80;

export interface GetCompactXAxis {
    interval: number;
    tick: FC<TickProps>;
}

export type GetCompactXAxisParams = (params: {
    visualizationsTheme: VisualizationsTheme;
    theme: Theme;
    dataLength: number;
    width: number;
    xAxisProps?: object;
    xTickFormatter: ValueFormatter<string>;
}) => GetCompactXAxis;

interface GetScaleThresholdParams {
    domain: number[];
    range: number[];
}

const getScaleThreshold = ({domain, range}: GetScaleThresholdParams) => (value: number): number =>
    range[domain.findIndex(breakpoint => value <= breakpoint)] || range[domain.length - 1];

export const getCompactXAxis: GetCompactXAxisParams = ({
    width,
    theme,
    xAxisProps,
    dataLength,
    visualizationsTheme,
    xTickFormatter = compactDateFormatter()
}) => {
    const {values} = theme.breakpoints;
    const isFmdBreakpoint = root.innerWidth < values.fmd;

    // Correlation between width/dataLength ratio and ticks interval
    const intervals = {
        [25]: 0,
        [15]: 1,
        [13]: 2,
        [8]: 3,
        [2]: 5
    };

    const interval = getScaleThreshold({
        domain: Object.keys(intervals).map(Number),
        range: Object.values(intervals)
    })(width / dataLength);

    return {
        interval,
        tick: (props: TickProps) => {
            const {x, dx, index, textAnchor, visibleTicksCount, payload, ...rest} = props;

            delete rest.verticalAnchor;
            delete rest.tickFormatter;

            const tickProps: {
                [key: string]: [string | number, string | number, string];
            } = {
                0: [isFmdBreakpoint ? 5 : 60, 0, 'start'], // Align first tick to the left for FMD breakpoint
                [visibleTicksCount - 1]: ['100%', -5, 'end'], // Align last tick to the right for FMD breakpoint
                rest: [x, dx, textAnchor] // Rest ticks align to center
            };

            const [xProps, dxProps, textAnchorProps] = tickProps[index] || tickProps.rest;

            const ticksCanBeRendered = width / AVERAGE_TICK_WIDTH;

            const showNthTick =
                ticksCanBeRendered > visibleTicksCount
                    ? 1 // Show all available ticks
                    : Math.floor(visibleTicksCount / ticksCanBeRendered);

            const isVisible = isFmdBreakpoint
                ? !index || index === visibleTicksCount - 1 // Show only first and last tick for FMD breakpoint
                : !(index % showNthTick); // Show each nth tick

            if (isVisible) {
                return (
                    <text
                        {...rest}
                        fill={visualizationsTheme.axisLabel}
                        fontSize={visualizationsTheme.axisFontSize}
                        x={xProps}
                        dx={dxProps}
                        textAnchor={textAnchorProps}>
                        {xTickFormatter(payload.value)}
                    </text>
                );
            }

            return null;
        },
        ...xAxisProps
    };
};
